import { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebaseConfig';
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    where,
    serverTimestamp
} from 'firebase/firestore';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';

function ChatAdmin({ user, adminMode = false, targetUserId = null }) {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [alias, setAlias] = useState(() => {
        try { return localStorage.getItem('chat_alias') || ''; }
        catch (e) { return ''; }
    });
    const [chatUid, setChatUid] = useState(null); // UID real de auth (anónimo o real)
    const scrollRef = useRef();

    const [connError, setConnError] = useState(null);

    // 1. Asegurar Autenticidad (Anónima o Real)
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                // Si el admin está viendo un chat, el ID del chat es el del usuario objetivo
                // Si es un usuario normal (real o anónimo), su propio UID es su ID de chat
                const uid = adminMode ? targetUserId : firebaseUser.uid;
                setChatUid(uid);
            } else if (!adminMode) {
                // Si no hay usuario y es modo cliente, iniciar sesión anónima
                signInAnonymously(auth).catch(e => {
                    console.error("Anonymous auth error:", e);
                    setConnError("No se pudo iniciar sesión segura.");
                });
            }
        });
        return () => unsubscribe();
    }, [adminMode, targetUserId]);

    // 2. Escuchar Mensajes
    useEffect(() => {
        if (!chatUid) return;
        setConnError(null);
        console.log("Starting listener for chatId:", chatUid);

        // Eliminamos orderBy para evitar requerir un índice compuesto
        const messagesQuery = query(
            collection(db, 'admin_chats'),
            where('chatId', '==', chatUid)
        );

        const unsubscribe = onSnapshot(messagesQuery,
            (snapshot) => {
                const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // ORDENAMIENTO ROBUSTO: Priorizar mensajes con timestamp, luego por orden de llegada (ID)
                const sortedMsgs = [...msgs].sort((a, b) => {
                    const tA = a.timestamp ? a.timestamp.toMillis() : Date.now();
                    const tB = b.timestamp ? b.timestamp.toMillis() : Date.now();
                    if (tA !== tB) return tA - tB;
                    return a.id.localeCompare(b.id);
                });
                console.log(`Received ${msgs.length} messages for ${chatUid}`);
                setMessages(sortedMsgs);
            },
            (err) => {
                console.error("CRITICAL: Error al escuchar mensajes:", err);
                if (err.code === 'permission-denied') {
                    setConnError("Error de Permisos: No tienes acceso a este chat.");
                } else if (err.code === 'failed-precondition') {
                    setConnError("Error de Índice: Falta configuración en la base de datos.");
                } else {
                    setConnError(`Error de Red/Firebase (${err.code}): Intenta refrescar.`);
                }
            }
        );

        return () => unsubscribe();
    }, [chatUid]);

    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (e) => {
        e.preventDefault();
        const currentUser = auth.currentUser;
        if (!newMessage.trim() || !chatUid || !currentUser || sending) return;

        setSending(true);
        const currentSenderName = adminMode ? (user?.displayName || 'Admin') : (user?.displayName || alias || 'Invitado');

        try {
            await addDoc(collection(db, 'admin_chats'), {
                chatId: chatUid,
                senderId: currentUser.uid,
                senderName: currentSenderName,
                text: newMessage,
                timestamp: serverTimestamp(),
                isAdmin: adminMode
            });
            setNewMessage('');

            if (!user && alias) {
                try { localStorage.setItem('chat_alias', alias); } catch (e) { }
            }
        } catch (error) {
            console.error("Error al enviar mensaje:", error);
            alert("No se pudo enviar. Revisa tu conexión a internet.");
        }
        setSending(false);
    };

    if (!chatUid && !adminMode) {
        return (
            <div className="flex items-center justify-center h-[400px] bg-neutral-900 border border-neutral-800 rounded-lg">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-500 text-sm italic">Inicializando conexión segura...</p>
                </div>
            </div>
        );
    }

    return (
        <div key={chatUid} className="flex flex-col h-[400px] bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden shadow-2xl">
            <div className="bg-neutral-800 p-3 border-b border-neutral-700 flex flex-col gap-2">
                <div className="flex justify-between items-center text-shadow-none">
                    <h3 className="font-bold text-sm flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${connError ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`}></span>
                        {adminMode ? `Chat con: ${targetUserId}` : 'Chat con Administrador'}
                    </h3>
                </div>
                {!user && !adminMode && (
                    <input
                        type="text"
                        placeholder="Tu Alias (ej: Juan Pérez)"
                        className="bg-black/40 border border-neutral-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-yellow-500 transition-all font-sans"
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                    />
                )}
                {connError && (
                    <p className="text-[10px] text-red-500 bg-red-500/10 p-1 rounded text-center font-bold tracking-tight">{connError}</p>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 font-josefin">
                {messages.length === 0 && !connError && (
                    <p className="text-center text-xs text-gray-500 mt-10 italic">
                        No hay mensajes todavía. Escribe algo para comenzar.
                    </p>
                )}
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex ${msg.senderId === auth.currentUser?.uid ? 'justify-end' : 'justify-start'}`}
                    >
                        <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${msg.senderId === auth.currentUser?.uid
                            ? 'bg-blue-600 text-white rounded-tr-none'
                            : 'bg-neutral-800 text-gray-200 rounded-tl-none border border-neutral-700'
                            }`}>
                            {adminMode && msg.senderId !== auth.currentUser?.uid && (
                                <p className="text-[10px] text-blue-400 font-bold mb-1 uppercase tracking-tighter">{msg.senderName}</p>
                            )}
                            <p className="leading-relaxed">{msg.text}</p>
                            <p className="text-[10px] opacity-50 mt-1 text-right">
                                {msg.timestamp
                                    ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : 'Recibiendo...'}
                            </p>
                        </div>
                    </div>
                ))}
                <div ref={scrollRef} />
            </div>

            <form onSubmit={sendMessage} className="p-3 bg-neutral-800/50 border-t border-neutral-700 flex gap-2">
                <input
                    type="text"
                    disabled={sending || !!connError}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={sending ? "Enviando..." : "Escribe un mensaje..."}
                    className="flex-1 bg-neutral-900 text-white text-sm border border-neutral-600 rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 transition-all text-shadow-none disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="bg-yellow-600 hover:bg-yellow-500 text-black p-2 rounded-full transition-colors shadow-lg disabled:bg-neutral-700"
                >
                    {sending ? (
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                        </svg>
                    )}
                </button>
            </form>
        </div>
    );
}

export default ChatAdmin;
